import PDFDocument from "pdfkit/js/pdfkit.standalone";
import qrcode from "qrcode-generator";
import { format } from "date-fns";
import { formatCnpjCpf } from "../../utils";
import { metodosPagamentosDfe } from "../listas/metodos-pagamentos-dfe";

function textoSeguro(valor) {
  return String(valor ?? "").trim();
}

function formatarValor(valor) {
  if (valor === undefined || valor === null || valor === "") return "0,00";
  const numero = parseFloat(valor);
  if (Number.isNaN(numero)) return "0,00";
  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatarDataHora(data) {
  if (!data) return "";
  if (typeof data === "string" && /^\d{2}\/\d{2}\/\d{4}/.test(data))
    return data;
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return textoSeguro(data);
  return format(d, "dd/MM/yyyy HH:mm:ss");
}

function numeroSeguro(valor) {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
}

function obterValorPago(nfSaidaPagamentos) {
  return nfSaidaPagamentos?.reduce((total, pagamento) => {
    return total + pagamento?.valorTitulo;
  }, 0);
}

function obterEnderecoEmitente(dadosEmpresa) {
  const endereco = dadosEmpresa?.enderecos?.[0] || dadosEmpresa?.endereco || {};
  const cidade = endereco?.cidade || dadosEmpresa?.cidade || {};
  const estado = cidade?.estado || dadosEmpresa?.estado || {};

  return {
    xLgr:
      endereco?.logradouro ||
      dadosEmpresa?.enderecoLogradouro ||
      dadosEmpresa?.logradouro ||
      "",
    nro:
      endereco?.numero ||
      dadosEmpresa?.enderecoLogradouroNumero ||
      dadosEmpresa?.numero ||
      "",
    xBairro:
      endereco?.bairro ||
      dadosEmpresa?.enderecoBairro ||
      dadosEmpresa?.bairro ||
      "",
    xMun: cidade?.nome || dadosEmpresa?.enderecoCidade || "",
    UF: estado?.sigla || dadosEmpresa?.enderecoUF || dadosEmpresa?.uf || "",
  };
}

function mapearParaDto(pdvEntity, dadosEmpresa) {
  const clienteNome =
    pdvEntity?.clienteNome ||
    pdvEntity?.clienteSelecionado?.nomeRazaoSocial ||
    "";
  const clienteCnpjCpf =
    pdvEntity?.clienteCnpjCpf || pdvEntity?.clienteSelecionado?.cnpjCpf || "";
  const itens = (pdvEntity?.nfSaidaItens || []).map((item) => {
    const valorUnitario = numeroSeguro(item?.valor);
    const quantidade = numeroSeguro(item?.quantidade);
    const subtotalInformado = item?.subtotal;
    const subtotalCalculado =
      subtotalInformado !== undefined &&
      subtotalInformado !== null &&
      subtotalInformado !== ""
        ? numeroSeguro(subtotalInformado)
        : valorUnitario * quantidade;

    return {
      prod: {
        cProd: textoSeguro(item?.codigo),
        xProd: textoSeguro(item?.descricao),
        qCom: quantidade,
        uCom: textoSeguro(item?.unidadeCom || item?.unidade || "UN"),
        vProd: valorUnitario,
        subtotal: subtotalCalculado,
      },
    };
  });
  const totalPago = obterValorPago(pdvEntity?.nfSaidaPagamentos);
  const totalVenda = numeroSeguro(pdvEntity?.totalVenda);
  return {
    itens,
    totalVenda: formatarValor(totalVenda),
    desconto: formatarValor(numeroSeguro(pdvEntity?.desconto)),
    despesas: formatarValor(numeroSeguro(pdvEntity?.despesas)),
    troco: formatarValor(totalPago - totalVenda),
    nfeChave: textoSeguro(pdvEntity?.chaveAcesso),
    qrCode: pdvEntity?.urlChave,
    nomeRazaoSocialDestinatario: textoSeguro(clienteNome),
    cnpjCpfDestinatario: textoSeguro(clienteCnpjCpf),
    numeroNota: textoSeguro(pdvEntity?.numeroDocumento),
    serie: textoSeguro(pdvEntity?.serieDocumento),
    dataHora: formatarDataHora(pdvEntity?.dataEmissao),
    protocolo: textoSeguro(pdvEntity?.protocolo || "PENDENTE"),
    cnpjCpfEmitente: textoSeguro(dadosEmpresa?.cnpjCpf),
    ufEmpresa: textoSeguro(dadosEmpresa?.enderecoUF),
    nomeRazaoSocialEmitente: textoSeguro(
      dadosEmpresa?.nomeRazaoSocial || dadosEmpresa?.fantasia,
    ),
    enderecoEmitente: obterEnderecoEmitente(dadosEmpresa),
    totalPago,
    ieEmitente: textoSeguro(
      dadosEmpresa?.inscricaoEstadual ||
        dadosEmpresa?.ie ||
        dadosEmpresa?.ieEmitente,
    ),
    nfSaidaPagamentos: (pdvEntity?.nfSaidaPagamentos || []).map((p) => ({
      descricao:
        metodosPagamentosDfe.find(
          (m) => Number(m.id) == Number(p?.meioPagamentoSelecionado?.codigo),
        )?.descricao || "OUTROS",
      valorTitulo: numeroSeguro(p?.valorTitulo),
    })),
  };
}

function concatenarBytes(chunks) {
  const tamanhoTotal = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const resultado = new Uint8Array(tamanhoTotal);
  let offset = 0;

  chunks.forEach((chunk) => {
    resultado.set(chunk, offset);
    offset += chunk.length;
  });

  return resultado;
}

function bytesParaBase64(bytes) {
  let binario = "";
  const tamanhoFatia = 0x8000;

  for (let indice = 0; indice < bytes.length; indice += tamanhoFatia) {
    const fatia = bytes.subarray(indice, indice + tamanhoFatia);
    binario += String.fromCharCode(...fatia);
  }

  return btoa(binario);
}

async function carregarLogoBuffer(dataUrl) {
  if (!dataUrl) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const pngDataUrl = canvas.toDataURL("image/png");
        const base64 = pngDataUrl.split(",")[1];
        const binario = atob(base64);
        const bytes = new Uint8Array(binario.length);
        for (let i = 0; i < binario.length; i++) {
          bytes[i] = binario.charCodeAt(i);
        }
        resolve(bytes.buffer);
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export async function generateDanfeNfceContigencia({
  pdvEntity,
  dadosEmpresa,
  imagemLogoBase64,
  meioPagamentoList = [],
}) {
  const dto = mapearParaDto(pdvEntity, dadosEmpresa, meioPagamentoList);
  const largura = 200;
  let inicio = 0;
  let inicioItem = 71;
  let inicioResulmoTotais = 100;
  const logoBuffer = await carregarLogoBuffer(imagemLogoBase64);
  const quantidadeItens = dto.itens.length;
  const quantidadePagamentos = dto.nfSaidaPagamentos?.length || 0;
  const comprimentoSemImagem =
    313 + 27 * quantidadeItens + 10 * quantidadePagamentos;
  let margenComImagem = 0;
  let comprimento = comprimentoSemImagem;
  if (logoBuffer) {
    margenComImagem = 15;
    comprimento += 80;
  }

  return new Promise((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: [203.77, comprimento],
        margin: 0,
        bufferPages: true,
        autoFirstPage: true,
        info: {
          Title: `DANFE - ${dto.numeroNota}`,
          Author: "SIAF Evolution",
          Subject: "Documento Auxiliar da Nota Fiscal Eletrônica",
          Keywords: "danfe, nfe, documento fiscal",
          Creator: "SIAF Evolution",
          Producer: "SIAF Evolution",
        },
      });

      const margemTopo = 10 + margenComImagem;
      const margemEsquerda = 1;
      const margemDireita = doc?.page?.width - 1;
      const ajusteX = 2;

      function linhaHorizontal(x1, x2, y) {
        y = margemTopo + y;
        x1 = margemEsquerda + x1;
        x2 = margemDireita + x2;
        doc.moveTo(x1, y).lineTo(x2, y).stroke();
      }

      function campo(
        string,
        x,
        y,
        larguraTexto = 100,
        alinhamento = "center",
        tamanho = 6,
      ) {
        string = string || "";
        x = margemEsquerda + x + ajusteX;
        y = margemTopo + y;
        doc
          .font("Helvetica-Bold")
          .fillColor("black")
          .fontSize(tamanho)
          .text(string, x, y, {
            width: larguraTexto,
            align: alinhamento,
          });
      }

      function normal(
        string,
        x,
        y,
        larguraTexto = 100,
        alinhamento = "center",
        tamanho = 6,
      ) {
        string = string || "";
        x = margemEsquerda + x + ajusteX;
        y = margemTopo + y;
        doc
          .font("Helvetica")
          .fillColor("black")
          .fontSize(tamanho)
          .text(string, x, y, {
            width: larguraTexto,
            align: alinhamento,
            lineGap: -1,
          });
      }

      function desenharRetanguloCinza(x, y, larguraRetangulo, altura) {
        doc
          .rect(margemEsquerda + x, margemTopo + y, larguraRetangulo, altura)
          .fillColor("gray")
          .opacity(0.2)
          .fill();
        doc.opacity(1);
      }

      function desenhaCabecalho() {
        normal(
          `CNPJ: ${formatCnpjCpf(dto.cnpjCpfEmitente) || ""} ${dto.nomeRazaoSocialEmitente || ""}`,
          1,
          inicio,
          largura,
          "center",
          6,
        );
        const enderecoCompleto = `${dto.enderecoEmitente?.xLgr}, ${dto.enderecoEmitente?.nro} - ${dto.enderecoEmitente?.xBairro}`;
        const municipioEstado = `${dto.enderecoEmitente?.xMun} - ${dto.enderecoEmitente?.UF}`;
        normal(enderecoCompleto, 1, inicio + 7, largura, "center", 6);
        normal(municipioEstado, 1, inicio + 14, largura, "center", 6);
        normal(`I.E: ${dto.ieEmitente}`, 1, inicio + 21, largura, "center", 6);
        campo(
          "DANFE NFC-E - DOCUMENTO AUXILIAR DA NOTA",
          1,
          inicio + 28,
          largura,
          "center",
          7,
        );
        campo(
          "FISCAL DE CONSUMIDOR ELETRÔNICA",
          1,
          inicio + 37,
          largura,
          "center",
          7,
        );
        campo("EMITIDA EM CONTINGÊNCIA", 1, inicio + 46, largura, "center", 8);
        campo("Pendente de Autorização", 1, inicio + 53, largura, "center", 7);
        desenharRetanguloCinza(1, inicio + 45, largura, 15);
      }

      function desenhaCabecalhoTabelaItens() {
        campo("CÓD", 3, inicio + 61, largura, "left", 7);
        campo("DESC", 30, inicio + 61, largura, "left", 7);
        campo("QTD", 85, inicio + 61, largura, "left", 7);
        campo("UN", 105, inicio + 61, largura, "left", 7);
        campo("UNIT", 123, inicio + 61, largura, "left", 7);
        campo("TOTAL", 165, inicio + 61, largura, "left", 7);
        linhaHorizontal(0, 0, inicio + 68);
      }

      function desenhaItem(produto) {
        const totalProduto =
          produto?.subtotal !== undefined
            ? Number(produto.subtotal)
            : Number(produto.vProd) * Number(produto.qCom);
        normal(produto.cProd, 3, inicioItem, largura - 2, "left", 6);
        normal(produto.xProd, 30, inicioItem, largura - 29, "left", 6);
        normal(
          formatarValor(produto.qCom),
          85,
          inicioItem + 15,
          largura - 109,
          "left",
          6,
        );
        normal(produto.uCom, 105, inicioItem + 15, largura - 30, "left", 6);
        normal(
          formatarValor(produto.vProd),
          123,
          inicioItem + 15,
          largura - 163,
          "left",
          6,
        );
        normal(
          formatarValor(totalProduto),
          165,
          inicioItem + 15,
          largura - 163,
          "left",
          6,
        );
        linhaHorizontal(0, 0, inicioItem + 23);
      }

      function desenhaResulmoTotal() {
        campo(
          "QTD. TOTAL DE ITENS",
          2,
          inicioResulmoTotais,
          largura - 105,
          "left",
          7,
        );
        campo(
          dto.itens.length,
          105,
          inicioResulmoTotais,
          largura - 110,
          "right",
          7,
        );
        campo(
          "SUBTOTAL",
          2,
          inicioResulmoTotais + 10,
          largura - 105,
          "left",
          7,
        );
        campo(
          dto.totalVenda,
          105,
          inicioResulmoTotais + 10,
          largura - 110,
          "right",
          7,
        );
        campo(
          "DESCONTO",
          2,
          inicioResulmoTotais + 20,
          largura - 105,
          "left",
          7,
        );
        campo(
          `-${dto.desconto}`,
          105,
          inicioResulmoTotais + 20,
          largura - 110,
          "right",
          7,
        );
        campo(
          "ACRÉSCIMOS",
          2,
          inicioResulmoTotais + 30,
          largura - 105,
          "left",
          7,
        );
        campo(
          `+${dto.despesas || "0,00"}`,
          105,
          inicioResulmoTotais + 30,
          largura - 110,
          "right",
          7,
        );
        campo("TOTAL", 2, inicioResulmoTotais + 40, largura - 105, "left", 7);
        campo(
          dto.totalVenda,
          105,
          inicioResulmoTotais + 40,
          largura - 110,
          "right",
          7,
        );
        dto.nfSaidaPagamentos?.forEach((pagamento, i) => {
          campo(
            pagamento.descricao,
            2,
            inicioResulmoTotais + 50 + 10 * i,
            largura - 105,
            "left",
            7,
          );
          campo(
            formatarValor(pagamento.valorTitulo),
            105,
            inicioResulmoTotais + 50 + 10 * i,
            largura - 110,
            "right",
            7,
          );
        });
        campo(
          "TOTAL PAGO",
          2,
          inicioResulmoTotais + 50 + 10 * quantidadePagamentos,
          largura - 105,
          "left",
          7,
        );
        campo(
          formatarValor(dto.totalPago),
          105,
          inicioResulmoTotais + 50 + 10 * quantidadePagamentos,
          largura - 110,
          "right",
          7,
        );
        campo(
          "TROCO",
          2,
          inicioResulmoTotais + 60 + 10 * quantidadePagamentos,
          largura - 105,
          "left",
          7,
        );
        campo(
          dto.troco,
          105,
          inicioResulmoTotais + 60 + 10 * quantidadePagamentos,
          largura - 110,
          "right",
          7,
        );
      }

      function desenhaInfoChaveAcesso() {
        normal(
          "CONSULTE PELA CHAVE DE ACESSO EM",
          1,
          inicioResulmoTotais + 70,
          largura,
          "center",
          6,
        );
        normal(
          `WWW.FAZENDA.${dto.ufEmpresa}.GOV.BR/NFCE/CONSULTA`.toUpperCase(),
          1,
          inicioResulmoTotais + 80,
          largura,
          "center",
          6,
        );
        normal(
          dto.nfeChave || "SEM CHAVE",
          1,
          inicioResulmoTotais + 90,
          largura,
          "center",
          6,
        );
      }

      async function desenhaQrCode() {
        if (!dto.qrCode) return;
        const qr = qrcode(0, "L");
        qr.addData(dto.qrCode);
        qr.make();

        const quantidadeModulos = qr.getModuleCount();
        const tamanhoModulo = 2;
        const inicioX = 6;
        const inicioY = inicioResulmoTotais + 115 + margenComImagem;

        for (let linha = 0; linha < quantidadeModulos; linha++) {
          for (let coluna = 0; coluna < quantidadeModulos; coluna++) {
            if (qr.isDark(linha, coluna)) {
              doc
                .rect(
                  inicioX + coluna * tamanhoModulo,
                  inicioY + linha * tamanhoModulo,
                  tamanhoModulo,
                  tamanhoModulo,
                )
                .fill("black");
            }
          }
        }
      }

      function desenhaInfoClienteENotaLateralQrCode() {
        normal(
          dto.nomeRazaoSocialDestinatario,
          85,
          inicioResulmoTotais + 102,
          largura - 85,
          "center",
          6,
        );
        normal(
          `CONSUMIDOR CPF: ${formatCnpjCpf(dto.cnpjCpfDestinatario) || ""}`,
          85,
          inicioResulmoTotais + 110,
          largura - 85,
          "center",
          6,
        );
        normal(
          "PESSOA FISICA - DENTRO",
          85,
          inicioResulmoTotais + 117,
          largura - 85,
          "center",
          6,
        );
        normal(
          `NFC-E N ${dto.numeroNota}`,
          85,
          inicioResulmoTotais + 135,
          largura - 85,
          "center",
          6,
        );
        normal(
          `SÉRIE ${dto.serie}`,
          85,
          inicioResulmoTotais + 143,
          largura - 85,
          "center",
          6,
        );
        normal(
          dto.dataHora,
          85,
          inicioResulmoTotais + 151,
          largura - 85,
          "center",
          6,
        );
        normal(
          "PROTOCOLO DE AUTORIZAÇÃO",
          85,
          inicioResulmoTotais + 159,
          largura - 85,
          "center",
          6,
        );
        normal(
          dto.protocolo || "PENDENTE",
          85,
          inicioResulmoTotais + 167,
          largura - 85,
          "center",
          6,
        );
        normal(
          "DATA DE AUTORIZAÇÃO",
          85,
          inicioResulmoTotais + 175,
          largura - 85,
          "center",
          6,
        );
        normal(
          dto.dataHora || "PENDENTE",
          85,
          inicioResulmoTotais + 183,
          largura - 85,
          "center",
          6,
        );
        normal(
          "DANFE NFC-E - DOCUMENTO",
          85,
          inicioResulmoTotais + 191,
          largura - 85,
          "center",
          6,
        );
        normal(
          "AUXILIAR DA NOTA FISCAL DE",
          85,
          inicioResulmoTotais + 199,
          largura - 85,
          "center",
          6,
        );
        normal(
          "CONSUMIDOR ELETRÔNICA",
          85,
          inicioResulmoTotais + 207,
          largura - 85,
          "center",
          6,
        );
      }

      function desenhaLogo() {
        if (!logoBuffer) return;
        try {
          doc.image(logoBuffer, 60, 5, {
            fit: [80, 80],
            align: "center",
            valign: "center",
          });
          inicio = 65;
          inicioItem = inicioItem + inicio;
          inicioResulmoTotais = inicioResulmoTotais + inicio;
        } catch (e) {
          return;
        }
      }

      const chunks = [];
      doc.on("data", (chunk) => {
        chunks.push(
          chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk),
        );
      });
      doc.on("error", (error) => reject(error));
      doc.on("end", () => {
        try {
          resolve(bytesParaBase64(concatenarBytes(chunks)));
        } catch (error) {
          reject(error);
        }
      });
      desenhaLogo();
      desenhaCabecalho();
      desenhaCabecalhoTabelaItens();
      for (let index = 0; index < quantidadeItens; index++) {
        if (index === 0) {
          desenhaItem(dto.itens[index].prod);
        } else {
          inicioItem += 27;
          inicioResulmoTotais += 27;
          desenhaItem(dto.itens[index].prod);
        }
      }
      desenhaResulmoTotal();
      inicioResulmoTotais += 10 * quantidadePagamentos;
      desenhaInfoChaveAcesso();
      desenhaQrCode();
      desenhaInfoClienteENotaLateralQrCode();
      linhaHorizontal(0, 0, inicioResulmoTotais + 217);
      normal(
        "NOTA GERADA PELO SIAF Evolution",
        0,
        inicioResulmoTotais + 222,
        largura,
        "center",
        6,
      );

      doc.end();
    } catch (error) {
      reject(error);
    }
  });
}
